<template>
  <div class="min-h-screen">
    <SegmentHero
      title="Seus dados, suas regras"
      description="Solicite acesso, correção, eliminação ou portabilidade dos seus dados pessoais, conforme a LGPD."
      title-class="text-white drop-shadow-md"
      primary-label=""
      primary-href=""
      secondary-label=""
      secondary-href=""
      disclaimer=""
      :background-image="heroBg"
      background-class="bg-center bg-cover bg-slate-900"
      overlay-variant="left"
    />

    <div class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-xl mx-auto">
          <p class="text-gray-600 mb-8">
            Se você é lead, cliente ou apenas navegou pelo nosso site, pode
            solicitar aqui o exercício de qualquer um dos seus direitos
            previstos no Art. 18 da LGPD. Respondemos em até
            <b>15 dias</b>. Veja também nossa
            <RouterLink
              to="/politica-de-privacidade"
              class="text-[#006086] font-semibold hover:underline"
              >Política de Privacidade</RouterLink
            >.
          </p>

          <div v-if="submitted" class="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
            <div class="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <svg class="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-gray-900 mt-4">Solicitação recebida</h3>
            <p class="text-sm text-gray-600 mt-1">
              Nossa equipe vai analisar seu pedido e responder em até 15 dias
              no e-mail informado.
            </p>
          </div>

          <form v-else @submit.prevent="submit" class="space-y-4" novalidate>
            <div aria-hidden="true" style="position: absolute; left: -9999px; top: -9999px; width: 0; height: 0; overflow: hidden">
              <input v-model="hpConfirmEmail" type="text" name="confirm_email" tabindex="-1" autocomplete="off" />
            </div>

            <div v-if="formError" class="bg-red-50 text-red-600 text-sm font-medium rounded-lg px-4 py-3">
              {{ formError }}
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1" for="dr_request_type">O que você quer solicitar? *</label>
              <select
                id="dr_request_type"
                v-model="form.request_type"
                class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md bg-white"
              >
                <option value="access">Acesso aos meus dados</option>
                <option value="correction">Correção de dados incorretos</option>
                <option value="portability">Portabilidade dos meus dados</option>
                <option value="deletion">Eliminação dos meus dados</option>
                <option value="revoke_consent">Revogação de consentimento</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1" for="dr_name">Nome completo *</label>
              <input
                id="dr_name"
                v-model="form.name" type="text" placeholder="Seu nome"
                class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1" for="dr_email">E-mail *</label>
              <input
                id="dr_email"
                v-model="form.email" type="email" placeholder="Seu e-mail"
                class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
              />
              <p class="text-xs text-gray-400 mt-1">Use o mesmo e-mail que você usou em contato conosco, se souber.</p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1" for="dr_phone">Telefone</label>
                <input
                  id="dr_phone"
                  v-model="form.phone" type="tel" placeholder="Seu telefone" @input="maskPhone"
                  class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1" for="dr_document">CPF/CNPJ</label>
                <input
                  id="dr_document"
                  v-model="form.document" type="text" placeholder="Opcional"
                  class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1" for="dr_details">Detalhes</label>
              <textarea
                id="dr_details"
                v-model="form.details" rows="4" placeholder="Descreva sua solicitação, se quiser (opcional)"
                class="py-2.5 px-4 text-sm block w-full shadow-sm focus:ring-kali-blue-500 focus:border-kali-blue-500 border border-gray-300 rounded-md"
              />
            </div>

            <button
              type="submit" :disabled="submitting"
              class="w-full bg-[#004d6b] text-white py-3 rounded-lg font-semibold hover:bg-[#003d55] transition disabled:opacity-60"
            >
              {{ submitting ? "Enviando..." : "Enviar solicitação" }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <CTA />
  </div>
</template>

<script setup>
import { ref } from "vue";
import CTA from "@/components/home/CTA.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/privacy-bg.webp";
import { useSeo } from "@/composables/useSeo";
import { usePhoneMask } from "@/composables/usePhoneMask";
import api from "@/services/api";

useSeo({
  title: "Meus Dados (LGPD) | Kali Sistemas",
  description:
    "Solicite acesso, correção, eliminação ou portabilidade dos seus dados pessoais, conforme a LGPD.",
  image: heroBg,
});

const { formatPhone } = usePhoneMask();

const form = ref({
  request_type: "access",
  name: "",
  email: "",
  phone: "",
  document: "",
  details: "",
});

const formError = ref("");
const submitting = ref(false);
const submitted = ref(false);

const hpConfirmEmail = ref("");
const formTs = Math.floor(Date.now() / 1000);

function maskPhone(e) {
  form.value.phone = formatPhone(e.target.value);
}

async function submit() {
  if (!form.value.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    formError.value = "Preencha nome e e-mail válidos.";
    return;
  }
  formError.value = "";
  submitting.value = true;
  try {
    await api.post("/public/data-rights", {
      ...form.value,
      confirm_email: hpConfirmEmail.value,
      form_ts: formTs,
      page_url: window.location.href,
    });
    submitted.value = true;
  } catch (e) {
    formError.value = e?.response?.data?.message || "Não foi possível enviar agora. Tente novamente.";
  } finally {
    submitting.value = false;
  }
}
</script>
